import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the ResignationContext
const ResignationContext = createContext();

// Custom hook to use the ResignationContext
export const useResignation = () => useContext(ResignationContext);

// Provider component to wrap the app
export const ResignationProvider = ({ children }) => {
  const [resignations, setResignation] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/resignations";
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchResignations = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}?page=${page}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const { data } = response.data;

        setResignation(data.data || []); // Update leave types list
        // console.log("resignations data", response.data);
        setPagination({
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          totalItems: response.data.data.total,
          perPage: response.data.data.per_page,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching resignations master:", error);
        setError(
          error.response?.data?.message || "Failed to fetch resignations.",
        );
      } finally {
        setLoading(false);
      }
    },
    [API_URL, token],
  );

  // Fetch departments master from the API on load
  useEffect(() => {
    fetchResignations();
  }, [fetchResignations]);

  // Add a new resignation
  const addResignation = async (newResignation) => {
    try {
      const response = await axios.post(API_URL, newResignation, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // Optimistically append locally if API doesn't return entity; then refresh in background
      const created = response?.data?.data;
      if (created) {
        setResignation((prev) => [created, ...prev]);
      }
      // If current page may not include the new item, propose redirect
      const redirectToFirstPage = pagination.currentPage !== 1;
      // Soft refresh current page to keep pagination stats fresh
      fetchResignations(pagination.currentPage);
      return {
        ok: true,
        message: "Resignation added successfully.",
        data: created || null,
        redirectToFirstPage,
      };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add resignation.";
      return { ok: false, message };
    }
  };

  // Update an existing department
  const updateResignation = async (updateResignation) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updateResignation.employee_id}`,
        updateResignation,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setResignation((prev) =>
        prev.map((resignation) =>
          resignation.id === updateResignation.id
            ? { ...resignation, ...response.data?.data }
            : resignation,
        ),
      );
      return {
        ok: true,
        message: "Resignation updated successfully.",
        data: response.data,
      };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update resignation.";
      return { ok: false, message };
      // console.error("Error updating resignation master:", error);
    }
  };

  // Delete a resignation
  const deleteResignation = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchResignations(pagination.currentPage);
      return { ok: true, message: "Resignation deleted successfully." };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete resignation.";
      return { ok: false, message };
    }
  };

  const takeActionOnResignation = async (id, actionPayload) => {
    try {
      const response = await axios.post(
        `${API_URL}/${id}/action`, // adjust endpoint if needed
        actionPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // setResignation((prev) =>
      // prev.map((resignation) =>
      //     resignation.id === id
      //     ? { ...resignation, ...response.data?.data }
      //     : resignation
      // )
      // );

      await fetchResignations(pagination.currentPage);
      const msg = `Resignation ${actionPayload.action}ed successfully.`;
      return { ok: true, message: msg, data: response.data };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to take action on resignation.";
      return { ok: false, message };
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      loading,
      error,
      pagination,
      resignations,
      addResignation,
      updateResignation,
      deleteResignation,
      takeActionOnResignation,
      fetchResignations,
    }),
    [
      resignations,
      fetchResignations,
      loading,
      error,
      pagination,
      addResignation,
      updateResignation,
      deleteResignation,
      takeActionOnResignation,
    ],
  );

  return (
    <ResignationContext.Provider value={value}>
      {children}
    </ResignationContext.Provider>
  );
};
